import React, { useState, useEffect } from 'react';
import Grid from '@mui/material/Grid';
import Tooltip from '@mui/material/Tooltip';
import HelpCenterIcon from '@mui/icons-material/HelpCenter';
import {
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Typography,
  Paper,
  Chip,
  CircularProgress,
  Card,
  Zoom,
  Slide,
  Snackbar,
  Alert,
  useTheme,
  useMediaQuery
} from "@mui/material";
import {
  PlayArrow as PlayArrowIcon,
  Stop as StopIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  AccessTime as AccessTimeIcon,
  Comment as CommentIcon,
  Warning as WarningIcon
} from "@mui/icons-material";
import axios from 'axios';
import Lottie from 'lottie-react';
import dinnerAnimation from './Images/dinner.json';
import teatimeAnimation from './Images/teatime.json';
import ViewJobPlan from './ViewJobPlan';
import ShiftRemarksButton from './shiftRemarksButton';
import gearanimation from './Images/gearicon.json';
import idletimeAnimation from './Images/idletime.json';
import PropTypes from 'prop-types';
import { alpha } from '@mui/material/styles';
import useSessionLossRedirect from './useSessionLossRedirect';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const PopupModal = ({
  jobCodes, username, jobNumber, customer, operation, product, startedTime,
  operation_code, operation_description, start_datetime, employee_id, machine_code
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const handleSessionLoss = useSessionLossRedirect();

  const [firstModalOpen, setFirstModalOpen] = useState(false);
  const [secondModalOpen, setSecondModalOpen] = useState(false);
  const [thirdModalOpen, setThirdModalOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);          // NEW: snapshot end time only once
  const [remarks, setRemarks] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "error", // "error" | "warning" | "info" | "success"
  });

  // Inline error rendered directly inside the active popup (more
  // contextual than the snackbar — error appears next to the action that
  // produced it). Cleared when the operator opens/closes a popup or
  // retries the action.
  const [inlineError, setInlineError] = useState("");

  const showSnackbar = (message, severity = "error") => {
    setSnackbar({ open: true, message, severity });
  };
  const closeSnackbar = (_, reason) => {
    if (reason === "clickaway") return;
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const CHARACTER_LIMIT = 50;

  // Format duration to HH:MM:SS
  const formatDuration = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Compute duration between two Date objects (in seconds)
  const diffSeconds = (from, to) => {
    if (!from || !to) return 0;
    return Math.max(0, Math.floor((to - from) / 1000));
  };

  // Theme-aware operation color
  const getOperationTypeInfo = (code) => {
    if (['IT-TT', 'IT-LT', 'IT-DT'].includes(code)) {
      return { type: "meal", color: theme.palette.warning.main };
    } else if (code.startsWith("IT-")) {
      return { type: "idle", color: theme.palette.info.main };
    } else {
      return { type: "setup", color: theme.palette.secondary.main };
    }
  };

  useEffect(() => {
    const fetchOngoingOperation = async () => {
      if (operation_code && operation_description && start_datetime && machine_code) {
        try {
          const response = await axios.get(`${API_BASE_URL}/Ongoingoperationlog`, {
            params: { employee_id, machine_code, start_datetime }
          });
          if (response.data.length > 0) {
            const ongoingOperation = response.data[0];
            setSelectedJob({
              code: ongoingOperation.operation_code,
              process: ongoingOperation.operation_description
            });
            setStartTime(new Date(ongoingOperation.start_datetime));
            setSecondModalOpen(true);
          }
        } catch (error) {
          console.error("Error fetching ongoing operation:", error);
          if (handleSessionLoss(error)) {
            showSnackbar("Session expired. Redirecting to login...", "error");
          }
          // Other errors stay silent here — operator didn't trigger this
          // fetch directly, so no need to alarm them on a transient failure.
        }
      }
    };
    fetchOngoingOperation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operation_code, operation_description, start_datetime, employee_id, machine_code]);

  const handleOpenFirstModal = (job) => {
    setInlineError(""); // clear any prior error from previous attempt
    setFirstModalOpen(true);
    setSelectedJob(job);
  };

  const handleProceed = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    setInlineError("");
    try {
      const machineCodeResponse = await axios.get(`${API_BASE_URL}/getMachineCode?username=${username}`);
      let operation_type;
      if (selectedJob.code.startsWith('IT-')) {
        operation_type = ['IT-TT', 'IT-LT', 'IT-DT'].includes(selectedJob.code) ? 'Meals' : 'Idle';
      } else {
        operation_type = 'Setup';
      }
      const payload = {
        employee_id: username,
        operation_code: selectedJob.code,
        operation_description: selectedJob.process,
        start_datetime: new Date().toISOString().slice(0, 19).replace('T', ' '),
        machine_code: machineCodeResponse.data.machineCode,
        active_status: 'Ongoing',
        job_number: jobNumber,
        customer, product,
        main_operation: operation,
        operation_type,
        JobStartedTime: startedTime
      };
      const response = await axios.post(`${API_BASE_URL}/addjobOperationLog`, payload, {
        headers: { 'Content-Type': 'application/json' },
      });
      if (response.status === 200) {
        const { start_datetime } = response.data;
        setStartTime(new Date(start_datetime));
        setFirstModalOpen(false);
        setSecondModalOpen(true);
      } else {
        console.error("Failed to send operation log:", response.data);
      }
    } catch (error) {
      console.error("Error sending operation log:", error);
      const sessionMsg = "Session expired. Redirecting to login...";
      const genericMsg = "Failed to start operation. Please try again.";
      if (handleSessionLoss(error)) {
        setInlineError(sessionMsg);
        showSnackbar(sessionMsg, "error");
      } else {
        setInlineError(genericMsg);
        showSnackbar(genericMsg, "error");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseFirstModal = () => setFirstModalOpen(false);

  const handleOpenThirdModal = () => {
    // Snapshot the end time ONCE when the user clicks "End Operation"
    setEndTime(new Date());
    setInlineError(""); // clear any prior error from previous attempt
    setSecondModalOpen(false);
    setThirdModalOpen(true);
  };

  const handleCancelThirdModal = () => {
    setThirdModalOpen(false);
    setSecondModalOpen(true);
  };

  const handleCloseThirdModal = async () => {
    setInlineError("");
    try {
      const endDateObj = endTime || new Date(); // fallback safety
      const endDateTime = endDateObj.toISOString().slice(0, 19).replace('T', ' ');
      const trimmedRemarks = remarks.replace(/\s{2,}/g, ' ').trim();
      const payload = {
        employee_id: username,
        start_datetime: startTime.toISOString().slice(0, 19).replace("T", " "),
        end_datetime: endDateTime,
        remarks: trimmedRemarks,
        active_status: 'Finished'
      };
      const response = await axios.post(`${API_BASE_URL}/updateOperationLog`, payload, {
        headers: { 'Content-Type': 'application/json' },
      });
      if (response.status === 200) {
        setSecondModalOpen(false);
        setThirdModalOpen(false);
        setRemarks('');
        setEndTime(null);
      } else {
        console.error("Failed to update operation log:", response.data);
      }
    } catch (error) {
      console.error("Error updating operation log:", error);
      const sessionMsg = "Session expired. Redirecting to login...";
      const genericMsg = "Failed to end operation. Please try again.";
      if (handleSessionLoss(error)) {
        setInlineError(sessionMsg);
        showSnackbar(sessionMsg, "error");
      } else {
        setInlineError(genericMsg);
        showSnackbar(genericMsg, "error");
      }
    }
  };

  const prioritizedJobCodes = jobCodes.filter(job => ['IT-TT', 'IT-LT', 'IT-DT'].includes(job.code));
  const otherJobCodes = jobCodes.filter(job => !['IT-TT', 'IT-LT', 'IT-DT'].includes(job.code));

  // THEME-AWARE styles (sizes unchanged)
  const gradientStyle = {
    background: `linear-gradient(90deg, ${alpha(theme.palette.info.light, 0.9)}, ${alpha(theme.palette.warning.light, 0.9)})`,
    backgroundSize: '200% 100%',
    animation: 'gradient 4s ease infinite',
    color: theme.palette.getContrastText(theme.palette.warning.main),
    borderColor: theme.palette.error.main,
  };

  const baseJobButtonStyle = {
    margin: '0px',
    padding: '0px',
    fontSize: '17px',
    fontWeight: 'bold',
    backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.24 : 0.10),
    color: theme.palette.mode === 'dark' ? theme.palette.primary.light : theme.palette.primary.dark,
    borderRadius: '15px',
    boxShadow: `0px 3px 5px ${alpha(theme.palette.primary.main, 0.5)}`
  };

  const gridItemStyle = {
    color: theme.palette.text.primary,
    margin: '5px 3px',
    padding: '2px'
  };

  const gearAnimationContainerStyle = {
    position: 'absolute',
    top: 0, left: 0,
    width: '100%', height: '100%',
    display: 'flex', justifyContent: 'center', alignItems: 'center',
    zIndex: 0,
  };

  return (
    <>
      <Grid container justifyContent="center" alignItems="center" >
        {[...prioritizedJobCodes, ...otherJobCodes].map((job) => (
          <Grid item key={job.code} style={gridItemStyle}>
            <Button
              style={{
                ...baseJobButtonStyle,
                ...(job.code === 'IT-TT' || job.code === 'IT-LT' || job.code === 'IT-DT' ? gradientStyle : {})
              }}
              variant="outlined"
              color="primary"
              onClick={() => handleOpenFirstModal(job)}
            >
              <div style={{ margin: '0px', padding: '0px', width: "80px" }}>
                {job.code}
              </div>
            </Button>
            <Tooltip style={{ margin: '0px', padding: '0px' }} title={job.process}>
              <IconButton style={{ margin: '0px', padding: '0.5px' }} color="secondary" aria-label="help center">
                <HelpCenterIcon />
              </IconButton>
            </Tooltip>
          </Grid>
        ))}
      </Grid>

      {/* First Modal */}
      <Dialog
        open={firstModalOpen}
        TransitionComponent={Transition}
        keepMounted
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '20px',
            backgroundColor: alpha(theme.palette.background.paper, 0.95),
            backdropFilter: 'blur(20px)',
            border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
            boxShadow: theme.shadows[12],
            overflow: 'hidden',
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0, left: 0, right: 0,
              height: '4px',
              background: selectedJob
                ? `linear-gradient(90deg, ${getOperationTypeInfo(selectedJob.code).color}, ${theme.palette.secondary.main})`
                : `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            }
          }
        }}
      >
        <DialogTitle sx={{ p: 0 }}>
          <Box sx={{
            p: 1, pb: 0.5, m: 0,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.10)} 0%, ${alpha(theme.palette.secondary.main, 0.10)} 100%)`,
            textAlign: 'center'
          }}>
            <Zoom in={firstModalOpen} timeout={500}>
              <Box>
                <PlayArrowIcon
                  sx={{
                    fontSize: 48,
                    color: selectedJob ? getOperationTypeInfo(selectedJob.code).color : theme.palette.primary.main,
                    mb: 1,
                    filter: `drop-shadow(0 4px 8px ${alpha(theme.palette.common.black, 0.2)})`
                  }}
                />
                <Typography variant="h5" fontWeight="600" color="text.primary">
                  Start Job Operation
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Confirm to begin the selected operation
                </Typography>
              </Box>
            </Zoom>
          </Box>
        </DialogTitle>

        <DialogContent sx={{
          p: 1, m: 0, textAlign: 'center',
          '& .MuiPaper-root': { m: 0 }
        }}>
          {inlineError && (
            <Alert
              severity="error"
              onClose={() => setInlineError("")}
              sx={{
                mb: 2,
                borderRadius: 2,
                fontWeight: 500,
                textAlign: "left",
              }}
            >
              {inlineError}
            </Alert>
          )}
          <Card
            elevation={0}
            sx={{
              p: 1, m: 0,
              backgroundColor: alpha(theme.palette.text.primary, 0.04),
              borderRadius: '16px',
              border: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
              mb: 1
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
              <Chip
                label={selectedJob?.code || ''}
                size="large"
                sx={{
                  fontSize: '1.2rem',
                  fontWeight: 700,
                  px: 2, py: 1,
                  backgroundColor: selectedJob ? getOperationTypeInfo(selectedJob.code).color : theme.palette.primary.main,
                  color: theme.palette.common.white,
                  '& .MuiChip-label': { px: 1 }
                }}
              />
            </Box>
            <Typography variant="h6" color="primary" fontWeight="600" sx={{ mb: 1 }}>
              {selectedJob?.process}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Operation will be logged for job tracking
            </Typography>
          </Card>

          {/* Static “now” display — not live-updating */}
          <Box sx={{
            display: 'flex', justifyContent: 'center', alignItems: 'center',
            gap: 1, p: 2,
            backgroundColor: alpha(theme.palette.success.main, 0.10),
            borderRadius: '12px',
            border: `1px solid ${alpha(theme.palette.success.main, 0.20)}`
          }}>
            <AccessTimeIcon sx={{ color: 'success.main', fontSize: 20 }} />
            <Typography variant="body2" color="success.main" fontWeight={600}>
              {new Date().toLocaleString()}
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 1, pt: 0, m: 0, justifyContent: 'center', gap: 2, '& .MuiButton-root': { m: 0 } }}>
          <Button
            onClick={handleProceed}
            variant="contained"
            size="large"
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <PlayArrowIcon />}
            sx={{
              minWidth: 140,
              borderRadius: '12px',
              py: 1.5,
              fontWeight: 600,
              textTransform: 'none',
              background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
              boxShadow: `0 6px 20px ${alpha(theme.palette.success.main, 0.4)}`,
              '&:hover': {
                background: `linear-gradient(135deg, ${theme.palette.success.dark} 0%, ${theme.palette.success.darker || theme.palette.success.dark} 100%)`,
                transform: 'translateY(-2px)',
                boxShadow: `0 8px 25px ${alpha(theme.palette.success.main, 0.5)}`
              }
            }}
          >
            {isSubmitting ? "Starting..." : "Proceed"}
          </Button>
          <Button
            onClick={handleCloseFirstModal}
            variant="outlined"
            size="large"
            startIcon={<CancelIcon />}
            sx={{
              minWidth: 140,
              borderRadius: '12px',
              py: 1.5,
              fontWeight: 600,
              textTransform: 'none',
              borderColor: theme.palette.error.main,
              color: theme.palette.error.main,
              '&:hover': {
                backgroundColor: alpha(theme.palette.error.main, 0.10),
                borderColor: theme.palette.error.dark,
                transform: 'translateY(-2px)'
              }
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Second Modal (in-progress) */}
      <Dialog
        open={secondModalOpen}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '24px',
            background: selectedJob
              ? `linear-gradient(145deg, ${alpha(getOperationTypeInfo(selectedJob.code).color, 0.15)} 0%, ${alpha(getOperationTypeInfo(selectedJob.code).color, 0.25)} 50%, ${alpha(theme.palette.background.default, 0.96)} 100%)`
              : `linear-gradient(145deg, ${alpha(theme.palette.primary.main, 0.20)} 0%, ${alpha(theme.palette.secondary.main, 0.30)} 50%, ${alpha(theme.palette.background.default, 0.96)} 100%)`,
            backdropFilter: 'blur(20px)',
            border: selectedJob
              ? `1px solid ${alpha(getOperationTypeInfo(selectedJob.code).color, 0.40)}`
              : `1px solid ${alpha(theme.palette.divider, 0.9)}`,
            boxShadow: theme.shadows[12],
            overflow: 'hidden',
            position: 'relative',
            minHeight: isMobile ? '400px' : '500px',
          }
        }}
      >
        <DialogTitle sx={{ p: 0 }}>
          <Box sx={{
            p: 1, m: 0,
            background: selectedJob
              ? `linear-gradient(135deg, ${alpha(getOperationTypeInfo(selectedJob.code).color, 0.30)} 0%, ${alpha(getOperationTypeInfo(selectedJob.code).color, 0.20)} 50%, ${alpha(theme.palette.background.default, 0.90)} 100%)`
              : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.30)} 0%, ${alpha(theme.palette.secondary.main, 0.30)} 50%, ${alpha(theme.palette.background.default, 0.90)} 100%)`,
            borderBottom: selectedJob
              ? `1px solid ${alpha(getOperationTypeInfo(selectedJob.code).color, 0.50)}`
              : `1px solid ${theme.palette.divider}`,
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <ShiftRemarksButton Employee_ID={username} />
                <ViewJobPlan username={username} />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {/* No live duration chip */}
                <Chip
                  icon={<AccessTimeIcon />}
                  label={`Started ${startTime ? startTime.toLocaleTimeString() : '--:--:--'}`}
                  sx={{
                    backgroundColor: alpha(theme.palette.common.white, 0.12),
                    color: theme.palette.common.white,
                    fontWeight: 600,
                    fontSize: '1rem'
                  }}
                />
              </Box>
            </Box>

            <Box sx={{ textAlign: 'center' }}>
              <Typography
                variant="h5"
                fontWeight="700"
                sx={{ color: theme.palette.common.white, textShadow: '0 2px 4px rgba(0,0,0,0.5)', mb: 1 }}
              >
                Operation in Progress
              </Typography>
              <Chip
                label={selectedJob?.code || ''}
                size="large"
                sx={{
                  fontSize: '1.1rem',
                  fontWeight: 700,
                  backgroundColor: selectedJob ? getOperationTypeInfo(selectedJob.code).color : theme.palette.primary.main,
                  color: theme.palette.common.white
                }}
              />
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 0, m: 0, position: 'relative', height: '400px', '& .MuiBox-root': { m: 0 } }}>
          {/* Animation Backgrounds (unchanged sizes) */}
          {selectedJob && !selectedJob.code.startsWith('IT-') && (
            <div style={gearAnimationContainerStyle}>
              <Lottie animationData={gearanimation} loop autoplay style={{ width: "100%", height: "100%" }} />
            </div>
          )}
          {selectedJob?.code?.startsWith('IT-') && !['IT-DT', 'IT-LT', 'IT-TT'].includes(selectedJob.code) && (
            <div style={gearAnimationContainerStyle}>
              <Lottie animationData={idletimeAnimation} loop autoplay style={{ width: "100%", height: "100%" }} />
            </div>
          )}
          {selectedJob && (selectedJob.code === 'IT-DT' || selectedJob.code === 'IT-LT') && (
            <div style={gearAnimationContainerStyle}>
              <Lottie animationData={dinnerAnimation} loop autoplay style={{ width: "100%", height: "100%" }} />
            </div>
          )}
          {selectedJob && selectedJob.code === 'IT-TT' && (
            <div style={gearAnimationContainerStyle}>
              <Lottie animationData={teatimeAnimation} loop autoplay style={{ width: "100%", height: "100%" }} />
            </div>
          )}

          {/* Overlay Content */}
          <Box sx={{
            position: 'absolute', bottom: 0, left: 0, right: 0,
            background: selectedJob
              ? `linear-gradient(transparent, ${alpha(getOperationTypeInfo(selectedJob.code).color, 0.20)} 30%, ${alpha(theme.palette.background.default, 0.96)} 100%)`
              : `linear-gradient(transparent, ${alpha(theme.palette.primary.main, 0.20)} 30%, ${alpha(theme.palette.background.default, 0.96)} 100%)`,
            p: 1, m: 0, textAlign: 'center', backdropFilter: 'blur(10px)',
          }}>
            <Typography
              variant="h4"
              fontWeight="700"
              sx={{ color: theme.palette.common.white, textShadow: '0 2px 8px rgba(0,0,0,0.8)', mb: 1 }}
            >
              {selectedJob?.process}
            </Typography>

            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
              <Paper sx={{
                px: 1, py: 0.5,
                background: selectedJob
                  ? `linear-gradient(135deg, ${alpha(getOperationTypeInfo(selectedJob.code).color, 0.25)} 0%, ${alpha(theme.palette.common.white, 0.15)} 100%)`
                  : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.25)} 0%, ${alpha(theme.palette.common.white, 0.15)} 100%)`,
                backdropFilter: 'blur(15px)',
                borderRadius: '12px',
                border: selectedJob
                  ? `1px solid ${alpha(getOperationTypeInfo(selectedJob.code).color, 0.40)}`
                  : `1px solid ${alpha(theme.palette.common.white, 0.25)}`,
                padding: 0,
                '& .MuiPaper-root': { padding: 0 }
              }}>
                <Typography variant="body1" fontWeight="600" sx={{ color: theme.palette.common.white }}>
                  Started: {startTime?.toLocaleTimeString()}
                </Typography>
              </Paper>
              {/* Removed live Duration box */}
            </Box>

            <Button
              onClick={handleOpenThirdModal}
              variant="contained"
              size="large"
              startIcon={<StopIcon />}
              sx={{
                minWidth: 200,
                borderRadius: '16px',
                py: 1.5,
                fontWeight: 700,
                fontSize: '1.1rem',
                textTransform: 'none',
                background: `linear-gradient(135deg, ${theme.palette.error.main} 0%, ${theme.palette.error.dark} 100%)`,
                boxShadow: `0 8px 25px ${alpha(theme.palette.error.main, 0.4)}`,
                '&:hover': {
                  background: `linear-gradient(135deg, ${theme.palette.error.dark} 0%, ${theme.palette.error.darker || theme.palette.error.dark} 100%)`,
                  transform: 'translateY(-2px)',
                  boxShadow: `0 12px 35px ${alpha(theme.palette.error.main, 0.5)}`
                }
              }}
            >
              End Operation
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Third Modal (end) */}
      <Dialog
        open={thirdModalOpen}
        TransitionComponent={Transition}
        keepMounted
        maxWidth="sm"
        fullWidth
        sx={{ zIndex: 1400 }}
        PaperProps={{
          sx: {
            borderRadius: '20px',
            backgroundColor: alpha(theme.palette.background.paper, 0.95),
            backdropFilter: 'blur(20px)',
            border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
            boxShadow: theme.shadows[12],
            overflow: 'hidden',
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0, left: 0, right: 0,
              height: '4px',
              background: `linear-gradient(90deg, ${theme.palette.error.main}, ${theme.palette.warning.main})`,
            }
          }
        }}
      >
        <DialogTitle sx={{ p: 0 }}>
          <Box sx={{
            p: 1, pb: 0.5, m: 0,
            background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.10)} 0%, ${alpha(theme.palette.warning.main, 0.10)} 100%)`,
            textAlign: 'center'
          }}>
            <Zoom in={thirdModalOpen} timeout={500}>
              <Box>
                <WarningIcon
                  sx={{
                    fontSize: 48,
                    color: theme.palette.error.main,
                    mb: 1,
                    filter: `drop-shadow(0 4px 8px ${alpha(theme.palette.error.main, 0.3)})`
                  }}
                />
                <Typography variant="h5" fontWeight="600" color="text.primary">
                  End Operation
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Confirm to complete and log this operation
                </Typography>
              </Box>
            </Zoom>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 1, m: 0, '& .MuiCard-root': { m: 0 } }}>
          {inlineError && (
            <Alert
              severity="error"
              onClose={() => setInlineError("")}
              sx={{
                mb: 2,
                borderRadius: 2,
                fontWeight: 500,
                textAlign: "left",
              }}
            >
              {inlineError}
            </Alert>
          )}
          <Card
            elevation={0}
            sx={{
              p: 1, m: 0,
              backgroundColor: alpha(theme.palette.text.primary, 0.04),
              borderRadius: '16px',
              border: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
              mb: 1,
              textAlign: 'center'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
              <Chip
                label={selectedJob?.code || ''}
                size="large"
                sx={{
                  fontSize: '1.1rem',
                  fontWeight: 700,
                  backgroundColor: selectedJob ? getOperationTypeInfo(selectedJob.code).color : theme.palette.primary.main,
                  color: theme.palette.common.white
                }}
              />
            </Box>
            <Typography variant="h6" color="primary" fontWeight="600" sx={{ mb: 1 }}>
              {selectedJob?.process}
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Total Duration: <strong>{formatDuration(diffSeconds(startTime, endTime))}</strong>
            </Typography>

            <Box sx={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              p: 2, backgroundColor: alpha(theme.palette.success.main, 0.10),
              borderRadius: '8px', fontSize: '0.9rem', padding: '8px',
              '& .MuiBox-root': { padding: 0 }
            }}>
              <span>Started: {startTime?.toLocaleTimeString()}</span>
              <span>Ending: {endTime ? endTime.toLocaleTimeString() : '--:--:--'}</span>
            </Box>
          </Card>

          <Box sx={{ position: 'relative' }}>
            <TextField
              fullWidth
              label="Operation Remarks"
              placeholder="Add any comments about this operation..."
              multiline
              rows={3}
              value={remarks}
              onChange={(e) => {
                if (e.target.value.length <= CHARACTER_LIMIT) setRemarks(e.target.value);
              }}
              InputProps={{
                startAdornment: (
                  <CommentIcon sx={{ color: 'action.active', mr: 1, my: 0.5 }} />
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  backgroundColor: alpha(theme.palette.text.primary, 0.04),
                }
              }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Optional: Add notes about this operation
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: remarks.length >= CHARACTER_LIMIT ? 'error.main' : 'text.secondary',
                  fontWeight: remarks.length >= CHARACTER_LIMIT ? 'bold' : 'normal'
                }}
              >
                {remarks.length}/{CHARACTER_LIMIT}
              </Typography>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 1, pt: 0, m: 0, justifyContent: 'center', gap: 2, '& .MuiButton-root': { m: 0 } }}>
          <Button
            onClick={handleCloseThirdModal}
            variant="contained"
            size="large"
            startIcon={<CheckCircleIcon />}
            sx={{
              minWidth: 160,
              borderRadius: '12px',
              py: 1.5,
              fontWeight: 600,
              textTransform: 'none',
              background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
              boxShadow: `0 6px 20px ${alpha(theme.palette.success.main, 0.4)}`,
              '&:hover': {
                background: `linear-gradient(135deg, ${theme.palette.success.dark} 0%, ${theme.palette.success.darker || theme.palette.success.dark} 100%)`,
                transform: 'translateY(-2px)',
                boxShadow: `0 8px 25px ${alpha(theme.palette.success.main, 0.5)}`,
              }
            }}
          >
            Complete Operation
          </Button>
          <Button
            onClick={handleCancelThirdModal}
            variant="outlined"
            size="large"
            startIcon={<CancelIcon />}
            sx={{
              minWidth: 140,
              borderRadius: '12px',
              py: 1.5,
              fontWeight: 600,
              textTransform: 'none',
              borderColor: theme.palette.error.main,
              color: theme.palette.error.main,
              '&:hover': {
                backgroundColor: alpha(theme.palette.error.main, 0.10),
                borderColor: theme.palette.error.dark,
                transform: 'translateY(-2px)',
              }
            }}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        // Sit above MUI Dialogs so the operator sees the message during
        // a session-loss redirect or a failed start/stop attempt.
        sx={{ zIndex: (t) => t.zIndex.modal + 1 }}
      >
        <Alert
          onClose={closeSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%", borderRadius: 2, fontWeight: 500 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <style>{`
        @keyframes gradient {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
      `}</style>
    </>
  );
};

PopupModal.propTypes = {
  jobCodes: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.string.isRequired,
      process: PropTypes.string.isRequired,
    })
  ).isRequired,
  username: PropTypes.string.isRequired,
  jobNumber: PropTypes.string,
  customer: PropTypes.string,
  operation: PropTypes.string,
  product: PropTypes.string,
  startedTime: PropTypes.string,
  operation_code: PropTypes.string,
  operation_description: PropTypes.string,
  start_datetime: PropTypes.string,
  employee_id: PropTypes.string,
  machine_code: PropTypes.string,
};

export default PopupModal;